import { JobTitleColumns, JobTitleData, jobTitleEntityName } from '@/backend/config/classifiers';
import { WidgetFormConfig } from '@juvare/common-form-ui/WidgetForm';
import { ClassifierStatus } from '@juvare/platform-sdk-react/backend/features/classifiers/shared';
import { ClassifierAdminPage, ClassifierAdminPageCsvRow, SlideoutFields } from '@juvare/platform-sdk-react/ui/features/classifiers';
import { usePlatformSdkReactTranslation } from '@juvare/platform-sdk-react/ui/hooks/usePlatformSdkReactTranslation';
import { GetResponse, Row } from '@juvare/platform-shared/features/storage';
import React from 'react';

function getEntityTitle(row: Row<JobTitleColumns>): string {
    return row.columns.name;
}

const EntityDetailsComponent: React.FC<{ response: GetResponse<JobTitleData, JobTitleColumns> }> = (props) => {
    const { t } = usePlatformSdkReactTranslation();
    return <SlideoutFields fields={[{ label: 'Status', value: t(`classifiers.statusEnum.${props.response.data.status}`) }]} />;
};

const formConfig: WidgetFormConfig = {
    key: '!form',
    type: 'form',
    children: [
        {
            type: 'form.text',
            key: 'name',
            label: 'Name',
            validate: {
                required: true,
            },
        },
        {
            type: 'form.text',
            key: 'description',
            label: 'Description',
        },
        {
            type: 'form.select',
            key: 'status',
            label: 'Status',
            validate: {
                required: true,
            },
            values: [
                {
                    value: 'active',
                    label: 'Active',
                },
                {
                    value: 'deprecated',
                    label: 'Deprecated',
                },
                {
                    value: 'retired',
                    label: 'Retired',
                },
            ],
        },
    ],
};

function mapCsvRowToData(row: ClassifierAdminPageCsvRow<JobTitleData>): Promise<ClassifierAdminPageCsvRow<JobTitleData>> {
    const name = (row.data[0] ?? '').trim();
    const description = (row.data[1] ?? '').trim().toLowerCase();
    const status = (row.data[2] ?? '').trim().toLowerCase();

    if (name.length === 0) {
        row.errors.push('Name is required');
    }

    if (status.length === 0) {
        row.errors.push('Status is required');
    }

    switch (status) {
        case 'active':
        case 'deprecated':
        case 'retired':
            break;
        default:
            row.errors.push('Invalid status, expected one of: active, deprecated, retired');
    }

    if (row.errors.length === 0) {
        row.object = {
            data: {
                name,
                description,
                status: status as ClassifierStatus,
            },
        };
    }

    return Promise.resolve(row);
}

async function mapDataToCsvRow(response: GetResponse<JobTitleData, JobTitleColumns>): Promise<string[]> {
    return [response.data.name, response.data.description ?? '', response.data.status];
}

const csvFileHeader = ['Name', 'Description', 'Status'];
const csvRowExample = ['Test', '', '{active|deprecated|retired}'];

export const JobTitleAdminPage: React.FC = () => {
    return (
        <ClassifierAdminPage
            entity={jobTitleEntityName}
            title='Job Title'
            fullTextSearchField='search'
            getEntityTitle={getEntityTitle}
            entityDetailsComponent={EntityDetailsComponent}
            formConfig={formConfig}
            csvFileHeader={csvFileHeader}
            csvRowExample={csvRowExample}
            mapCsvRowToData={mapCsvRowToData}
            mapDataToCsvRow={mapDataToCsvRow}
        />
    );
};
